import { observer } from 'mobx-react-lite';

import { WebSidebarToggle } from '@/components/blocks/Layout/WebSidebarToggle';
import { WindowControls } from '@/components/blocks/Layout/WindowControls';
import { pageHeaderLeadingTunnel } from '@/design-system-v2/page-header-tunnel';
import { NavRailPageTitle } from '@/features/nav-rail/nav-rail-page-title';
import { tabStore } from '@/features/tabs/tabs.store';
import { cn } from '@/lib/utils';
import { inElectron } from '@/utils';

import { XStack } from './layout';

interface HeaderProps {
  /** The content to be displayed on the left side of the header */
  left?: React.ReactNode;
  /** The content to be displayed on the right side of the header */
  right?: React.ReactNode;
  className?: string;
}

/**
 * Header example:
 * ```typescriptreact
 * <Header left={<div>Left</div>} right={<div>Right</div>} />
 * ```
 * */
export const Header = observer(function Header({
  left,
  right,
  className,
}: HeaderProps) {
  // No navbar on web, so surface the sidebar toggle in the Header.
  const showWebSidebarToggle = !inElectron();
  left ??= <NavRailPageTitle />;

  return (
    <XStack
      center
      className={cn(
        'w-full min-w-0 px-2 items-center justify-between',
        className
      )}
    >
      {showWebSidebarToggle || left ? (
        <XStack className="items-center gap-2 min-w-0 flex-[1_0_auto]">
          <WebSidebarToggle />
          {left}
          <pageHeaderLeadingTunnel.Out />
          <div className="flex-1 h-full">&nbsp;</div>
        </XStack>
      ) : (
        <div className="flex-1 h-full">&nbsp;</div>
      )}
      <XStack className="min-w-0 items-center">
        {right}
        {!tabStore.showTabBar && <WindowControls />}
      </XStack>
    </XStack>
  );
});
